import { useState } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import Container from '@/components/Container';
import Button from '@/components/Button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import FieldError from '@/components/FieldError';
import { useToast } from '@/components/ui/use-toast';
import { requestMfaReset } from '@/services/auth/auth.service';
import { useMfaTexts } from '../mfaTexts';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Self-service MFA recovery — step 1. Reached from the second-factor prompt via a
 * link, so it inherits the short-lived `mfaToken` (router state) minted after the
 * password step: the reset email can only be requested by someone who signed in.
 * The user confirms their email; the backend emails a one-time code (generic
 * response either way). On success we move on to the code screen.
 */
export default function MfaReset() {
  const location = useLocation();
  const mfaToken = (location.state as { mfaToken?: string } | null)?.mfaToken;
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const { toast } = useToast();
  const t = useMfaTexts();

  // The challenge token expired (or is missing): the user must sign in again.
  const isSessionError = (msg?: string | null) => !!msg && /session/i.test(msg);

  if (!mfaToken) return <Navigate to="/login" replace />;

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    const trimmed = email.trim();
    if (!EMAIL_RE.test(trimmed)) {
      setError(t.resetErrorFallback);
      return;
    }
    setLoading(true);
    const { data, error: err } = await requestMfaReset(mfaToken, trimmed);
    setLoading(false);
    if (err || !data) {
      if (isSessionError(err?.message)) {
        navigate('/login', { replace: true });
        return;
      }
      setError(err?.message || t.resetErrorFallback);
      return;
    }
    toast({ description: t.resetSentToast });
    navigate('/login/mfa/reset/code', { state: { email: trimmed } });
  };

  return (
    <Container className="h-full">
      <div className="col-span-12 flex min-h-0 items-center justify-center py-4">
        <div className="flex max-h-[90dvh] w-full max-w-[430px] flex-col items-start overflow-auto rounded-2xl bg-white p-8 shadow-lg">
          <h2 className="mb-2 font-haffer text-sh-30-38 font-bold leading-9 tracking-tight text-black-200">
            {t.resetRequestTitle}
          </h2>
          <p className="mb-8 font-haffer text-sh-sm text-dark-grey">{t.resetRequestDescription}</p>

          <form className="flex w-full flex-col items-start" onSubmit={onSubmit}>
            <div className="mb-6 w-full">
              <Label>
                <p className="mb-2 font-haffer text-sh-xs text-dark-grey">{t.resetEmailLabel}</p>
              </Label>
              <Input
                type="email"
                autoComplete="email"
                placeholder={t.resetEmailPlaceholder}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              {error && <FieldError message={error} />}
            </div>

            <Button type="submit" theme="solid" className="mb-4" showChevron={false} disabled={loading}>
              {loading ? t.loadingLabel : t.resetRequestCta}
            </Button>
          </form>

          <Link
            className="text-center font-haffer text-sh-xs font-bold text-people-teal-600 hover:!text-people-blue-600"
            to="/login">
            {t.backToSignInCta}
          </Link>
        </div>
      </div>
    </Container>
  );
}
